import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Folder, ExternalLink, Filter, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { seoAnalytics, TopCategoryItem } from '@/lib/api'
import { cn } from '@/lib/utils'

interface TopCategoriesTableProps {
  days: number
  section?: string
}

const SOURCE_OPTIONS = [
  { value: '', label: 'Todas las fuentes' },
  { value: 'google_organic', label: 'Google Organico' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'bing_organic', label: 'Bing Organico' },
  { value: 'direct', label: 'Trafico Directo' },
  { value: 'referral', label: 'Referencias' },
]

function getConversionRateColor(rate: number): string {
  if (rate >= 2) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
  if (rate >= 0.5) return 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
  return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
}

function getConversionRateIcon(rate: number) {
  if (rate >= 2) return TrendingUp
  if (rate >= 0.5) return Minus
  return TrendingDown
}

export default function TopCategoriesTable({ days, section }: TopCategoriesTableProps) {
  const [sourceFilter, setSourceFilter] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['seo-top-categories', days, sourceFilter, section],
    queryFn: () => seoAnalytics.getTopCategories(days, sourceFilter || undefined, section, 20),
  })

  const categories = data?.data?.categories || []
  const totals = data?.data?.totals

  const truncateName = (name: string, maxLength: number = 40) => {
    if (!name) return '-'
    if (name.length <= maxLength) return name
    return name.substring(0, maxLength) + '...'
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30">
            <Folder className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Top Categorias por Trafico</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Categorias con mas visitas y metricas de conversion
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="input input-sm w-44"
          >
            {SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card-body p-0">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse text-slate-400">Cargando datos...</div>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <Folder className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay datos de categorias</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Visitas
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Unicos
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Conv.
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tasa %
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    AOV
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {categories.map((cat: TopCategoryItem, idx: number) => {
                  const RateIcon = getConversionRateIcon(cat.conversion_rate)
                  const rateColorClass = getConversionRateColor(cat.conversion_rate)
                  return (
                    <tr
                      key={cat.prestashop_id || idx}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Folder className="w-4 h-4 text-purple-500 flex-shrink-0" />
                          <div>
                            <a
                              href={`https://www.jvs-informatica.com${cat.url_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
                              title={cat.category_name}
                            >
                              {truncateName(cat.category_name)}
                              <ExternalLink className="w-3 h-3 opacity-50" />
                            </a>
                            <div className="text-xs text-slate-400 mt-0.5">
                              ID: {cat.prestashop_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {cat.visits?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {cat.unique_visitors?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {cat.conversions || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                          rateColorClass
                        )}>
                          <RateIcon className="w-3 h-3" />
                          {cat.conversion_rate?.toFixed(2)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {formatCurrency(cat.revenue || 0)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {formatCurrency(cat.avg_order_value || 0)}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {totals && (
                <tfoot className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
                  <tr>
                    <td className="py-3 px-4">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        TOTALES
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        {totals.visits?.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {totals.unique_visitors?.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {totals.conversions || 0}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold',
                        getConversionRateColor(totals.conversion_rate)
                      )}>
                        {totals.conversion_rate?.toFixed(2)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        {formatCurrency(totals.revenue || 0)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {formatCurrency(totals.avg_order_value || 0)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {categories.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm text-slate-500">
            Mostrando {categories.length} categorias - Ultimos {days} dias
          </div>
        )}
      </div>
    </div>
  )
}
